import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePWA } from '@/hooks/usePWA';
import { Download, Share2, Smartphone, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PWAInstallPromptProps {
  onDismiss?: () => void;
  variant?: 'card' | 'banner' | 'modal';
}

export function PWAInstallPrompt({ onDismiss, variant = 'card' }: PWAInstallPromptProps) {
  const { canInstall, installApp, shareApp, platform, iosInstructions } = usePWA();
  const [isInstalling, setIsInstalling] = useState(false);
  const { toast } = useToast();

  const handleInstall = async () => {
    setIsInstalling(true);
    const result = await installApp();
    
    if (result.success) {
      toast({
        title: "App Installed!",
        description: "mySuccession has been added to your device",
      });
      onDismiss?.();
    } else {
      toast({
        title: "Installation Failed",
        description: result.error || "Unable to install the app",
        variant: "destructive",
      });
    }
    setIsInstalling(false);
  };

  const handleShare = async () => {
    const result = await shareApp();
    
    if (result.success) {
      toast({
        title: result.fallback === 'clipboard' ? "Link Copied!" : "Shared Successfully",
        description: result.fallback === 'clipboard' 
          ? "The app link has been copied to your clipboard"
          : "Thanks for sharing mySuccession",
      });
    } else {
      toast({
        title: "Share Failed",
        description: result.error || "Unable to share the app",
        variant: "destructive",
      });
    }
  };

  // Don't show if already installed or can't install
  if (!canInstall && !iosInstructions) {
    return null;
  }

  const content = (
    <>
      <CardHeader className="flex flex-row items-center space-y-0 pb-4">
        <div className="flex items-center space-x-2 flex-1">
          <Smartphone className="h-5 w-5 text-primary" />
          <div>
            <CardTitle className="text-lg">Install mySuccession</CardTitle>
            <CardDescription>
              {platform === 'ios' 
                ? "Add to your home screen for quick access"
                : "Install the app for better performance and offline access"
              }
            </CardDescription>
          </div>
        </div>
        {onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {iosInstructions ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {iosInstructions.instructions}
            </p>
            <div className="flex space-x-2">
              <Button
                onClick={handleShare}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share App
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex space-x-2">
            <Button
              onClick={handleInstall}
              disabled={isInstalling}
              size="sm"
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              {isInstalling ? "Installing..." : "Install App"}
            </Button>
            <Button
              onClick={handleShare}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
        )}
        
        <div className="text-xs text-muted-foreground">
          Benefits: Faster loading, offline access, push notifications
        </div>
      </CardContent>
    </>
  );

  if (variant === 'banner') {
    return (
      <div className="bg-primary/5 border-l-4 border-primary p-4 mb-6">
        <Card className="border-0 shadow-none bg-transparent">
          {content}
        </Card>
      </div>
    );
  }

  return <Card className="w-full max-w-md">{content}</Card>;
}